import { ReactNode, createContext, useContext, useMemo, useRef } from "react";
import type { ChatbotPromptProps as Props } from "./types";
import cuid from "cuid";
import {
  ChatbotConfigSchema,
  ChatbotConfigWithExtras as Context,
} from "./schema";

const ChatbotContext = createContext<Context<Props> | null>(null);

export function ChatbotContextProvider({
  children,
  ...props
}: Omit<Props, "chatbotSessionId"> & { children: ReactNode }) {
  const chatbotSessionId = useRef(cuid());

  const value = useMemo<Context<Props>>(() => {
    const config = ChatbotConfigSchema.parse(props.config);
    return {
      ...props,
      ...config,
      chatbotSessionId: chatbotSessionId.current,
      isStaticMode: config.defaultMessageMode === "static",
      triggers: (config.triggers ?? []).map(
        (trigger) => trigger.config as Required<typeof trigger.config>
      ),
      user: props.accountId?.toString(),
    };
  }, [props]);

  return (
    <ChatbotContext.Provider value={value}>{children}</ChatbotContext.Provider>
  );
}

export function useChatbotContext() {
  const context = useContext(ChatbotContext);
  if (context === null) {
    throw new Error(
      "useChatbotContext must be used within a ChatbotContextProvider"
    );
  }
  return context;
}
